<template>
  <section
    v-if="!hideBlock"
    ref="$block"
    class="osk-stories-block"
  >
    <div class="osk-wrapper">
      <h2
        v-if="data.title"
        class="osk-stories-block__title"
      >
        {{ data.title }}
      </h2>

      <UikitLoaderBlock
        v-if="!homeSocialData && !homeSocialError"
        class="osk-stories-block__loader"
      />

      <UikitErrorBlock
        v-else-if="homeSocialError"
        not-found
        :error="homeSocialError"
      />

      <UikitSwiper
        ref="$swiper"
        navigation
        off
        :breakpoints="sliderBreakpoints"
        :css-mode="!isDesktop"
        class="osk-stories-block__swiper"
        :load-more="getHomeSocialPending"
        @slide-change="handleSlideChange"
      >
        <UikitSwiperSlide
          v-for="(item, index) in contentList"
          :key="index"
        >
          <div
            class="osk-stories-block__card"
            :style="{
              'background-image': `url(${item.imgPath}`
            }"
            @click="openStory(index, item)"
          >
            <div class="osk-stories-block__card-text">
              {{ item.title }}
            </div>

            <Transition name="fade">
              <UikitLoaderInner v-if="index === loadingIndex" />
            </Transition>
          </div>
        </UikitSwiperSlide>
      </UikitSwiper>

      <Transition name="fade">
        <div
          v-if="isStoriesActive"
          class="osk-stories-block__wrapper"
        >
          <div class="osk-wrapper">
            <img
              src="~assets/img/oskelly-white.svg"
              alt="Oskelly logo"
              class="osk-stories-block__logo"
            >

            <div
              class="osk-stories-block__close"
              @click="closeStories"
            >
              <UikitIcon
                name="Cross"
                size="m"
                color="white"
              />
            </div>

            <BusinessStoriesMainSlider
              v-model:story-index="storyIndex"
              v-model:is-stories-active="isStoriesActive"
              :stories="storyList"
              :users-ids="usersIds"
              :analytics="analytics"
              @main-slide-change="handleMainSlideChange"
            />
          </div>
        </div>
      </Transition>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { Swiper as SwiperClass } from 'swiper/types';

import {
  PrimaryContentDTO, SocialDTO, StoriesDTO, StoryDTO, ContentType,
} from '~/restAPI/data-contracts';
import { useApi } from '~/restAPI';
import { Home } from '~/restAPI/Home';
import { Stories } from '~/restAPI/Stories';

import { AnalyticsEvents } from '~/types/analytics';

interface IAnalytics {
  chapter?: ContentType;
  chapter_index?: number;
  segment_id?: number;
  id_chapter?: string;
  item?: string;
}

const sliderBreakpoints = {
  0: {
    slidesPerView: 2.3,
    slidesPerGroup: 2,
    spaceBetween: 0,
    navigation: { enabled: false },
  },
  478: {
    slidesPerView: 3,
    slidesPerGroup: 3,
    spaceBetween: 0,
    navigation: { enabled: false },
  },
  580: {
    slidesPerView: 3.5,
    slidesPerGroup: 3,
    spaceBetween: 0,
    navigation: { enabled: false },
  },
  700: {
    slidesPerView: 4,
    slidesPerGroup: 4,
    spaceBetween: 0,
    navigation: { enabled: false },
  },
  1024: {
    slidesPerView: 6,
    slidesPerGroup: 6,
    spaceBetween: 24,
    navigation: { enabled: true },
  },
};

const props = defineProps<{
  data: PrimaryContentDTO
}>();

const hideBlock = ref(false);
const $block = ref<ComponentPublicInstance>();
const $swiper = ref();

const contentList = ref<SocialDTO[]>([]);

const isStoriesActive = ref(false);
const storyIndex = ref<number | null>(null);
const anchor = ref<string | undefined>();
const nextStoriesAnchor = ref<string | undefined>('0');
const storyList = ref<StoryDTO[]>([]);
const loadingIndex = ref<number>();

const { $addEvent } = useNuxtApp();
const { isDesktop } = useUiBreakpoints();
const { preventBodyScroll } = usePreventScroll();

const allIds = computed(() => contentList.value.map((i) => i.id!));

const analytics = computed<IAnalytics>(() => ({
  chapter: props.data.contentType,
  chapter_index: props.data.chapterIndex,
  segment_id: props.data.segmentId,
  id_chapter: props.data.id,
  item: 'story',
}));

const { event, clickEvent } = useSwiperAnalytics(
  $swiper,
  $block,
  allIds,
  analytics.value,
  sliderBreakpoints,
);

const { createApiInstance } = useApi();
const homeApi = createApiInstance(Home);
const storiesApi = createApiInstance(Stories);

const content = computed(() => (props.data.content ?? {}) as StoriesDTO);

const usersIds = computed(() => content.value.users?.map((u) => u.id as string) ?? []);

const {
  error: homeSocialError,
  data: homeSocialData,
  pending: getHomeSocialPending,
  refresh: getHomeSocial,
} = useLazyAsyncData(async () => {
  const res = await homeApi.getApiV2HomeSocial({
    anchor: anchor.value,
    count: 12,
    disableFeed: content.value.disableFeed,
    showStreams: false,
    selectedIds: usersIds.value,
  });

  anchor.value = res.data.data?.nextAnchor;

  if (res.data.data?.contentList) {
    contentList.value.push(...res.data.data.contentList);
  }

  if (!contentList.value.length) {
    hideBlock.value = true;
  }

  return res.data.data;
});

const {
  refresh: getStories,
} = useLazyAsyncData(async () => {
  const res = await storiesApi.getStoriesAllDataByUserViewStateNewPageUsingGet({
    anchor: nextStoriesAnchor.value,
    nextCount: 12,
    disableFeed: content.value.disableFeed,
    selectedIds: usersIds.value,
  });

  nextStoriesAnchor.value = res.data?.nextAnchor;

  if (res.data?.storyList) {
    storyList.value.push(...res.data.storyList);
  }

  return res.data;
}, { immediate: false });

function handleSlideChange(swiper: SwiperClass) {
  event();

  if (isDesktop.value && swiper.isEnd && anchor.value) {
    getHomeSocial();
  }

  if (!isDesktop.value && anchor.value && swiper.activeIndex % 8 === 0) {
    getHomeSocial();
  }
}

async function handleMainSlideChange(swiper: SwiperClass) {
  if (nextStoriesAnchor.value && swiper.activeIndex
  && (Number(nextStoriesAnchor.value) - 4 < swiper.activeIndex && swiper.activeIndex < Number(nextStoriesAnchor.value))) {
    await getStories();
  }
}

async function openStory(index: number, item: SocialDTO) {
  clickEvent({
    id_item: item.id,
    index,
    item: 'story',
  });

  loadingIndex.value = index;

  nextStoriesAnchor.value = String(index);

  await getStories();

  isStoriesActive.value = true;
  loadingIndex.value = undefined;
}

function closeStories(e: Event) {
  isStoriesActive.value = false;
  storyList.value = [];
  nextStoriesAnchor.value = '0';
  storyIndex.value = null;

  $addEvent(AnalyticsEvents.ClickEvent, {
    ...analytics.value,
    action: 'close',
    page: 'stories',
  });
}

watch(isStoriesActive, (value) => {
  preventBodyScroll(value);
});

onUnmounted(() => {
  preventBodyScroll(false);
  contentList.value = [];
});
</script>

<style scoped lang="scss">
@import "~/assets/scss/settings/index";

.osk-stories-block {
  padding-bottom: 42px;

  @include media-query(lg-and-up) {
    padding-bottom: 82px;
  }

  .osk-wrapper {
    padding: 0;

    @include media-query(md-and-up) {
      padding: 0 16px;
    }
  }

  &__loader {
    padding: 50px 0;
  }

  &__logo {
    display: none;

    @include media-query(md-and-up) {
      width: 100px;
      position: absolute;
      top: 52px;
      left: 52px;
      z-index: 2;
      display: block;
    }
  }

  &__title {
    @include font-heading;
    padding: 0 16px 22px;

    @include media-query(md-and-up) {
      text-transform: uppercase;
      text-align: center;
      padding: 7px 0 34px;
    }
  }

  &__card {
    border-radius: 2px;
    overflow: hidden;
    height: 236px;
    position: relative;
    background-size: cover;
    background-repeat: no-repeat;

    @include media-query(md-and-up) {
      border-radius: 4px;
      overflow: hidden;
      height: 320px;

      &:hover {
        cursor: pointer
      }
    }

    &:before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, .3);
      z-index: 1;
    }
  }

  &__card-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;

    @include media-query(md-and-up) {
      padding-top: 0;
    }
  }

  &__card-text {
    text-align: center;
    font-weight: 600;
    color: #fff;
    position: absolute;
    bottom: 16px;
    width: 100%;
    z-index: 2;
  }

  &__swiper {
    :deep(.swiper-slide) {
      padding: 0 16px;
      margin-right: -20px;

      @include media-query(lg-and-up) {
        padding: 0;
        margin: 0;
      }
    }
  }

  &:deep(.osk-swiper_off) {
    .swiper-wrapper {
      overflow-x: auto;
    }

    .swiper-slide {
      width: 180px !important;
      max-width: 282px;
      margin-right: 24px;
    }
  }

  &__wrapper {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1002;
    display: flex;
    background-color: rgba(#000, 0.8);

    @include media-query(md-and-up) {
      overflow-y: auto;
    }

    .osk-wrapper {
      @include media-query(md-and-up) {
        height: 100%;
        display: flex;
        height: 777px;
        margin: auto;
      }
    }
  }

  &__wrapper-content {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    overflow: hidden;
  }

  &__close {
    position: absolute;
    cursor: pointer;
    background: rgba(0,0,0,.2);
    border-radius: 100px;
    padding: 4px;
    right: 16px;
    top: 20px;
    z-index: 4;

    .osk-icon {
      width: 20px;
      height: 20px;
      color: #fff;
    }

    @include media-query(md-and-up) {
      top: 52px;
      right: 52px;
      background: none;
    }
  }
}
</style>
